
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';

export interface MissionParticipant {
  id: string;
  user_id: string;
  mission_id: string;
  status: 'ongoing' | 'completed' | 'expired';
  current_day: number;
  started_at: string;
  completed_at?: string;
  user: {
    username: string;
    main_photo_url?: string;
  };
  progress: {
    total_days: number;
    completed_days: number;
    completion_percentage: number;
    last_activity: string;
  };
}

export const useAdminMissionParticipants = (missionId: string) => {
  debugLogger.debug('useAdminMissionParticipants called with missionId:', missionId);

  const { data: participants, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-mission-participants', missionId],
    queryFn: async () => {
      debugLogger.debug('Fetching mission participants for mission:', missionId);
      
      if (!missionId) {
        throw new Error('Mission ID is required');
      }

      try {
        // Get user missions for this mission
        const { data: userMissions, error: userMissionsError } = await supabase
          .from('user_missions')
          .select('*')
          .eq('mission_id', missionId)
          .order('started_at', { ascending: false });

        if (userMissionsError) {
          debugLogger.error('Error fetching user missions:', userMissionsError);
          throw userMissionsError;
        }

        if (!userMissions || userMissions.length === 0) {
          debugLogger.debug('No user missions found for mission:', missionId);
          return [];
        }

        debugLogger.debug('Found user missions:', userMissions.length);

        // Get user profiles for all participants
        const userIds = userMissions.map(um => um.user_id);
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, username, main_photo_url')
          .in('id', userIds);

        if (profilesError) {
          debugLogger.error('Error fetching profiles:', profilesError);
        }

        debugLogger.debug('Found profiles:', profiles?.length || 0);

        // Get progress for each user mission
        const participantsWithProgress = await Promise.all(
          userMissions.map(async (userMission) => {
            const { data: progressData, error: progressError } = await supabase
              .from('user_mission_progress')
              .select('*')
              .eq('user_mission_id', userMission.id);

            if (progressError) {
              debugLogger.error(`Error fetching progress for user mission ${userMission.id}:`, progressError);
            }

            const progress = progressData || [];
            const completedDays = progress.filter(p => p.status === 'completed').length;
            const totalDays = 10; // Standard mission length

            // Find the profile for this user
            const userProfile = profiles?.find(p => p.id === userMission.user_id);

            const participant: MissionParticipant = {
              id: userMission.id,
              user_id: userMission.user_id,
              mission_id: userMission.mission_id,
              status: userMission.status as 'ongoing' | 'completed' | 'expired',
              current_day: userMission.current_day,
              started_at: userMission.started_at,
              completed_at: userMission.completed_at,
              user: {
                username: userProfile?.username || 'Usuário',
                main_photo_url: userProfile?.main_photo_url
              },
              progress: {
                total_days: totalDays,
                completed_days: completedDays,
                completion_percentage: Math.round((completedDays / totalDays) * 100),
                last_activity: userMission.updated_at || userMission.started_at
              }
            };

            return participant;
          })
        );

        debugLogger.debug('Final participants processed:', participantsWithProgress.length);
        return participantsWithProgress;

      } catch (error) {
        debugLogger.error('Error in mission participants query:', error);
        throw error;
      }
    },
    enabled: !!missionId,
    retry: 2,
    staleTime: 30000, // 30 seconds
    refetchOnMount: true,
    refetchOnWindowFocus: false
  });

  debugLogger.debug('Hook return values:', {
    participantsLength: participants?.length || 0,
    loading: isLoading,
    hasError: !!error
  });

  return {
    participants: participants || [],
    loading: isLoading,
    error,
    refetch
  };
};
